import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { messaging } from "../services/messaging.js";
import { useCallStore } from "../state/call.js";
import { blurModeAvailable, getBlurMode, setBlurMode, type BlurMode } from "../privacy.js";
import {
  enableNotifications,
  hasPushSubscription,
  notificationPermission,
  prepareNotifications,
  notificationSupport,
  sendTestNotification,
} from "../notifications.js";

type Status =
  | "checking"
  | "on"
  | "not-registered"
  | "off"
  | "blocked"
  | "ios-needs-install"
  | "unsupported";

const STATUS_TEXT: Record<Status, string> = {
  checking: "Checking…",
  on: "On for this device",
  "not-registered": "Allowed, but this device isn't registered yet",
  off: "Not turned on",
  blocked: "Blocked",
  "ios-needs-install": "Only available in the Home Screen app",
  unsupported: "Not supported in this browser",
};

async function currentStatus(): Promise<Status> {
  const support = notificationSupport();
  if (support !== "supported") return support;
  const permission = notificationPermission();
  if (permission === "denied") return "blocked";
  if (permission === "default") return "off";
  return (await hasPushSubscription()) ? "on" : "not-registered";
}

const BLUR_CHOICES: Array<{ value: BlurMode; title: string; detail: string }> = [
  {
    value: "hold",
    title: "Only while touching (strongest)",
    detail:
      "Readable while your finger is on the screen, blurred the moment you lift it — so the app switcher can never show a message.",
  },
  {
    value: "until-leave",
    title: "Until I leave TrueWave",
    detail:
      "Touch & hold to show the chat; it stays visible until you leave the app. More comfortable, but the app switcher can then show it (iPhone takes that picture before TrueWave can react).",
  },
  { value: "off", title: "Never blur", detail: "Chats are always readable on screen." },
];

/** Notifications status + "send a test", and the app version. */
export function Settings() {
  const [status, setStatus] = useState<Status>("checking");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; text: string } | null>(null);
  const [blur, setBlur] = useState(getBlurMode);
  const lastCallProblem = useCallStore((s) => s.lastCallProblem);

  const refresh = () => {
    void currentStatus().then(setStatus);
  };
  useEffect(refresh, []);
  // Fetch what registering needs now, so the tap can register instantly (iOS
  // refuses it after a slow wait — see notifications.ts).
  useEffect(() => {
    void prepareNotifications().catch(() => undefined);
  }, []);

  async function turnOn() {
    const account = messaging.account;
    if (!account) return;
    setBusy(true);
    setResult(null);
    try {
      await enableNotifications(account);
    } catch (err) {
      setResult({ ok: false, text: err instanceof Error ? err.message : "Couldn't turn on notifications." });
    } finally {
      setBusy(false);
      refresh();
    }
  }

  async function test() {
    const account = messaging.account;
    if (!account) return;
    setBusy(true);
    setResult(null);
    try {
      const r = await sendTestNotification(account);
      if (r.sent > 0) {
        setResult({
          ok: true,
          text: `Sent to ${r.sent} device${r.sent === 1 ? "" : "s"}. A "New message" banner should appear within a few seconds — lock your phone straight away to check the lock screen too.`,
        });
      } else {
        setResult({
          ok: false,
          text:
            r.devices === 0
              ? "The server has no registered device for you yet. Close TrueWave fully, reopen it from the Home Screen and try again."
              : "The notification service refused the test. Turn notifications off and on again for TrueWave, then retry.",
        });
      }
    } catch (err) {
      setResult({ ok: false, text: err instanceof Error ? err.message : "The test failed." });
    } finally {
      setBusy(false);
      refresh();
    }
  }

  return (
    <>
      <div className="topbar">
        <Link to="/"><button className="topbar-icon-btn" title="Back" aria-label="Back">←</button></Link>
        <strong className="grow">Settings</strong>
      </div>

      <div className="settings">
        <section className="card">
          <h1>Notifications</h1>
          <div className="settings-row">
            <span>Status</span>
            <strong className={status === "on" ? "ok" : status === "checking" ? "" : "warn"}>{STATUS_TEXT[status]}</strong>
          </div>

          {(status === "off" || status === "not-registered") && (
            <button className="primary" onClick={() => void turnOn()} disabled={busy}>
              {status === "off" ? "Turn on notifications" : "Register this device"}
            </button>
          )}
          {(status === "on" || status === "not-registered") && (
            <button onClick={() => void test()} disabled={busy}>
              {busy ? "Sending…" : "Send test notification"}
            </button>
          )}
          {result && <p className={result.ok ? "settings-ok" : "error"}>{result.text}</p>}

          {status === "ios-needs-install" && (
            <p>
              In Safari tap Share (the square with an arrow) → <em>Add to Home Screen</em>, then open TrueWave from
              that icon and come back here.
            </p>
          )}
          {status === "blocked" && (
            <p>Notifications were declined. Turn them on in your phone&apos;s Settings → Notifications → TrueWave.</p>
          )}

          <div className="settings-help">
            <strong>For banners on the lock screen (iPhone)</strong>
            <span>Settings → Notifications → TrueWave, then turn on:</span>
            <ul>
              <li>Allow Notifications</li>
              <li>Lock Screen, Notification Centre and Banners</li>
              <li>Sounds</li>
            </ul>
            <span>Focus / Do Not Disturb hides them unless TrueWave is allowed there.</span>
          </div>
        </section>

        {blurModeAvailable() && (
          <section className="card">
            <h1>Privacy</h1>
            <p>Chats open blurred, unreadable to anyone glancing over. Choose when they show:</p>
            {BLUR_CHOICES.map((choice) => (
              <label className="settings-toggle" key={choice.value}>
                <input
                  type="radio"
                  name="blur-mode"
                  checked={blur === choice.value}
                  onChange={() => {
                    setBlurMode(choice.value);
                    setBlur(choice.value);
                  }}
                />
                <span>
                  <strong>{choice.title}</strong>
                  <span>{choice.detail}</span>
                </span>
              </label>
            ))}
          </section>
        )}

        <p className="settings-version">Version {__APP_VERSION__}</p>
        {lastCallProblem && <p className="settings-diagnostic">Last call problem — {lastCallProblem}</p>}
      </div>
    </>
  );
}
